import React, { useRef, useState } from "react";
import { Upload, X, ImageIcon, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const MAX_IMAGES = 6;
const MAX_FILE_BYTES = 8 * 1024 * 1024;
const MAX_TOTAL_BYTES = 24 * 1024 * 1024;
const ACCEPT = ["image/jpeg", "image/png", "image/webp"];

export const UploadZone = ({ images, setImages }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const readFiles = (files) => {
    const supplied = Array.from(files);
    const unsupported = supplied.filter((f) => !ACCEPT.includes(f.type));
    const oversized = supplied.filter((f) => ACCEPT.includes(f.type) && f.size > MAX_FILE_BYTES);
    if (unsupported.length) toast.error("Only JPEG, PNG, and WebP images are supported.");
    if (oversized.length) toast.error("Each image must be 8 MB or smaller.");
    const validFiles = supplied.filter((f) => ACCEPT.includes(f.type) && f.size <= MAX_FILE_BYTES);
    let remainingBytes = MAX_TOTAL_BYTES - images.reduce((sum, image) => sum + (image.byteSize || 0), 0);
    const list = [];
    for (const file of validFiles) {
      if (file.size <= remainingBytes) {
        list.push(file);
        remainingBytes -= file.size;
      }
    }
    if (list.length < validFiles.length) toast.error("Images must be 24 MB or smaller in total.");
    if (list.length === 0) return;
    const slots = MAX_IMAGES - images.length;
    const chosen = list.slice(0, slots);
    Promise.all(
      chosen.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () =>
              resolve({
                id: `${file.name}-${Date.now()}-${Math.random()}`,
                name: file.name,
                byteSize: file.size,
                dataUrl: reader.result,
              });
            reader.readAsDataURL(file);
          })
      )
    ).then((res) => setImages((prev) => [...prev, ...res].slice(0, MAX_IMAGES)));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    readFiles(e.dataTransfer.files);
  };

  const remove = (id) => setImages((prev) => prev.filter((i) => i.id !== id));
  const full = images.length >= MAX_IMAGES;

  return (
    <div className="flex flex-col gap-4" data-testid="upload-section">
      <div
        data-testid="upload-zone"
        onClick={() => !full && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`relative flex flex-col items-center justify-center w-full h-44 rounded-xl border-2 border-dashed transition-colors ${
          full
            ? "border-zinc-200 dark:border-zinc-800 opacity-60 cursor-not-allowed"
            : dragging
            ? "border-zinc-900 dark:border-zinc-200 bg-zinc-100 dark:bg-zinc-800/60 cursor-pointer"
            : "border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900/50 hover:bg-zinc-100 dark:hover:bg-zinc-800/50 cursor-pointer"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          data-testid="upload-input"
          onChange={(e) => { readFiles(e.target.files); e.target.value = ""; }}
        />
        <Upload className="h-6 w-6 text-zinc-400 mb-2" strokeWidth={1.75} />
        <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
          {full ? "Maximum of 6 images reached" : "Drag & drop or click to upload"}
        </p>
        <p className="text-xs text-zinc-400 mt-1">JPEG · PNG · WEBP — 8 MB each, 24 MB total</p>
      </div>

      <div className="flex items-start gap-2 text-xs text-zinc-500 dark:text-zinc-400">
        <ImageIcon className="h-3.5 w-3.5 mt-0.5 shrink-0" strokeWidth={1.75} />
        <span>Recommended: 2–6 clear, well-lit photos from different angles of the same item.</span>
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3" data-testid="thumbnail-grid">
          {images.map((img) => (
            <div key={img.id} className="relative group aspect-square rounded-lg overflow-hidden border border-zinc-200 dark:border-zinc-800">
              <img src={img.dataUrl} alt={img.name} className="w-full h-full object-cover" />
              <button
                data-testid={`remove-image-${img.id}`}
                onClick={(e) => { e.stopPropagation(); remove(img.id); }}
                className="absolute top-1.5 right-1.5 p-1 rounded-md bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remove image"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {images.length === 1 && (
        <div
          data-testid="single-image-warning"
          className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30 px-3.5 py-3"
        >
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <p className="text-xs leading-relaxed text-amber-800 dark:text-amber-300">
            <span className="font-semibold">Single image detected.</span> Analysis from one view has a higher risk
            of skewed geometry and inaccurate component estimation. Add a second angle for better results.
          </p>
        </div>
      )}
    </div>
  );
};
